import { cn } from "@volca/ui/lib/utils";
import { SectionHeading } from "@/profitability/records/section-heading";
import { STATUS_DOT } from "@/profitability/review/finding-status";
import { STATUS_LABEL } from "@/profitability/review/labels";
import type { FindingStatus } from "@/profitability/review/types";

const SEGMENTS: { status: FindingStatus; bar: string }[] = (
  ["resolved", "explained", "investigating", "action_required", "open"] as const
).map((status) => ({ status, bar: STATUS_DOT[status] }));

/**
 * How far the review has got: every finding of the template as one bar,
 * split by where each stands. Handled is resolved or explained.
 */
export const ReviewProgress = ({
  counts,
}: {
  counts: Partial<Record<FindingStatus, number>>;
}) => {
  const total = SEGMENTS.reduce(
    (sum, item) => sum + (counts[item.status] ?? 0),
    0,
  );
  if (!total) return null;
  const handled = (counts.resolved ?? 0) + (counts.explained ?? 0);
  return (
    <section aria-label="Review progress" className="flex flex-col gap-2">
      <SectionHeading>Review progress</SectionHeading>
      <p className="text-sm text-ink tabular-nums">
        {handled} of {total} handled
      </p>
      <div className="flex h-1.5 gap-0.5" aria-hidden>
        {SEGMENTS.map((item) => {
          const value = counts[item.status] ?? 0;
          return value ? (
            <span
              key={item.status}
              className={cn("rounded-full", item.bar)}
              style={{ flexGrow: value }}
            />
          ) : null;
        })}
      </div>
      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-subtle tabular-nums">
        {SEGMENTS.filter((item) => counts[item.status]).map((item) => (
          <li key={item.status} className="flex items-center gap-1.5">
            <span
              className={cn("size-1.5 rounded-full", item.bar)}
              aria-hidden
            />
            {STATUS_LABEL[item.status]} {counts[item.status]}
          </li>
        ))}
      </ul>
    </section>
  );
};
